import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, MessageCircle, Globe, Calendar, Users } from 'lucide-react';
import Button from '../../components/base/Button';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    travelDates: '',
    groupSize: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const emailBody = `
Nouveau message de contact depuis le site web

Nom: ${formData.name}
Email: ${formData.email}
Téléphone: ${formData.phone || 'Non renseigné'}
Sujet: ${formData.subject}
Nombre de personnes: ${formData.groupSize || 'Non renseigné'}
Dates de voyage: ${formData.travelDates || 'Non renseigné'}

Message:
${formData.message}
      `.trim();

      const mailtoLink = `mailto:contact@mvd.perle-in.com?subject=${encodeURIComponent(`Contact: ${formData.subject}`)}&body=${encodeURIComponent(emailBody)}`;
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        travelDates: '',
        groupSize: ''
      });
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert('Une erreur est survenue. Veuillez nous contacter directement par email ou WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Téléphone & WhatsApp",
      content: "+971 50 62 11 791",
      link: "https://web.whatsapp.com/send?phone=971506211791",
      description: "Disponible 7j/7 de 9h à 21h (heure de Dubaï)",
      color: "bg-green-100 text-green-600"
    },
    {
      icon: Mail,
      title: "Email",
      content: "contact@mvd.perle-in.com",
      link: "mailto:contact@mvd.perle-in.com",
      description: "Réponse garantie sous 24h",
      color: "bg-blue-100 text-blue-600"
    },
    {
      icon: MapPin,
      title: "Adresse",
      content: "Dubai Silicon Oasis, Dubai, UAE",
      link: "https://maps.google.com/?q=Dubai+Silicon+Oasis",
      description: "Équipe française basée à Dubaï",
      color: "bg-amber-100 text-amber-600"
    },
    {
      icon: Clock,
      title: "Horaires",
      content: "7j/7 - 9h à 21h",
      description: "Support client en français",
      color: "bg-purple-100 text-purple-600"
    }
  ];

  if (isSubmitted) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />
        <section className="pt-6 pb-20 flex-grow flex items-center justify-center">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="bg-white rounded-2xl p-12 shadow-xl">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Message envoyé avec succès !
              </h2>
              <p className="text-gray-600 mb-8 text-lg">
                Notre équipe vous répondra dans les plus brefs délais. 
                En attendant, n'hésitez pas à nous contacter sur WhatsApp pour une réponse immédiate.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => setIsSubmitted(false)}
                  variant="outline"
                >
                  Envoyer un autre message
                </Button>
                <a
                  href="https://web.whatsapp.com/send?phone=971506211791"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button>
                    <MessageCircle size={20} className="mr-2" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-6 pb-12 bg-gradient-to-r from-blue-600 to-cyan-600 text-white relative">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <h1 className="text-4xl lg:text-5xl font-bold mb-2">Contactez-Nous</h1>
          <p className="text-lg text-blue-50 max-w-2xl">
            Notre équipe française basée à Dubaï est là pour vous accompagner dans votre projet de voyage
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 flex-grow">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Informations de Contact
                </h2>
                <div className="space-y-4">
                  {contactInfo.map((info, index) => (
                    <div 
                      key={index} 
                      className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`${info.color} p-3 rounded-lg flex-shrink-0`}>
                          <info.icon size={24} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{info.title}</h3>
                          {info.link ? (
                            <a
                              href={info.link}
                              target={info.link.startsWith('http') ? '_blank' : undefined}
                              rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                              className="text-amber-600 hover:text-amber-700 font-medium transition-colors block mb-1"
                            >
                              {info.content}
                            </a>
                          ) : (
                            <p className="text-gray-900 font-medium mb-1">{info.content}</p>
                          )}
                          <p className="text-gray-600 text-sm">{info.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-6 text-white">
                <h3 className="font-semibold mb-4 text-lg">Besoin d'une réponse immédiate ?</h3>
                <div className="space-y-3">
                  <a
                    href="https://web.whatsapp.com/send?phone=971506211791"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button variant="secondary" className="w-full">
                      <MessageCircle size={20} className="mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                  <a
                    href="tel:+971506211791"
                    className="block"
                  >
                    <Button variant="outline" className="w-full border-white text-white hover:bg-white/20 hover:text-white">
                      <Phone size={20} className="mr-2" />
                      Appeler maintenant
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Envoyez-nous un message
                </h2>
                <p className="text-gray-600 mb-8">
                  Remplissez le formulaire ci-dessous et notre équipe vous répondra rapidement
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                        placeholder="Votre nom"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                        placeholder="votre@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        Sujet *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                      >
                        <option value="">Choisir un sujet</option>
                        <option value="devis">Demande de devis</option>
                        <option value="reservation">Réservation</option>
                        <option value="information">Information activité</option>
                        <option value="modification">Modification/Annulation</option>
                        <option value="reclamation">Réclamation</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="travelDates" className="block text-sm font-medium text-gray-700 mb-2">
                        <Calendar size={16} className="inline mr-1" />
                        Dates de voyage
                      </label>
                      <input
                        type="text"
                        id="travelDates"
                        name="travelDates"
                        value={formData.travelDates}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                        placeholder="Ex: 15-22 mars 2025"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="groupSize" className="block text-sm font-medium text-gray-700 mb-2">
                        <Users size={16} className="inline mr-1" />
                        Nombre de personnes
                      </label>
                      <select
                        id="groupSize"
                        name="groupSize"
                        value={formData.groupSize}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                      >
                        <option value="">Sélectionner</option>
                        <option value="1">1 personne</option>
                        <option value="2">2 personnes</option>
                        <option value="3-4">3-4 personnes</option>
                        <option value="5-8">5-8 personnes</option>
                        <option value="9+">9+ personnes</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      maxLength={1000}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors resize-none"
                      placeholder="Décrivez votre demande, vos préférences, votre budget..."
                    />
                    <div className="text-right text-sm text-gray-500 mt-1">
                      {formData.message.length}/1000 caractères
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    loading={isSubmitting}
                    disabled={!formData.name || !formData.email || !formData.subject || !formData.message || isSubmitting}
                  >
                    {isSubmitting ? (
                      'Envoi en cours...'
                    ) : (
                      <>
                        <Send size={20} className="mr-2" />
                        Envoyer le message
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    En envoyant ce formulaire, vous acceptez d'être contacté par notre équipe 
                    concernant votre demande. Vos données sont protégées et ne seront jamais partagées.
                  </p>
                </form>
              </div>
            </div>
          </div>

          {/* Map - Full Width of Page */}
          <div className="mt-12 bg-white rounded-xl overflow-hidden shadow-md">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.0!2d55.2708!3d25.2048!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDEyJzE3LjMiTiA1NcKwMTYnMTQuOSJF!5e0!3m2!1sfr!2sfr!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full"
            ></iframe>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

